import axios from 'axios';

// Em desenvolvimento: localhost:3002
// Em produção: URL do Railway
const envApiUrl = import.meta.env.VITE_API_BASE_URL;
export const API_BASE_URL = envApiUrl || 'http://localhost:3002';

// Configure axios defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Debug: log do erro
    console.error('🚨 API Error:', error.response?.status, error.response?.data || error.message);

    if (error.response?.status === 401) {
      // Token expired or invalid, clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Não redirecionar automaticamente se já estivermos na página de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { email: string; senha: string }) =>
    api.post('/api/auth/login', credentials),
  
  register: (userData: any) =>
    api.post('/api/auth/register', userData),
  
  logout: () =>
    api.post('/api/auth/logout'),
  
  me: () =>
    api.get('/api/auth/me'),
  
  verificarUsuario: (id: number, verificado: boolean) =>
    api.patch(`/api/auth/verificar/${id}`, { verificado }),
};

// Vagas API
export const vagasAPI = {
  listar: (params?: {
    busca?: string;
    localizacao?: string;
    estado?: string;
    vagaFixa?: boolean;
    valorMinimo?: number;
    estagio?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/api/vagas', { params }),
  
  criar: (vaga: any) =>
    api.post('/api/vagas', vaga),
  
  // Aceita id numérico ou slug — a URL pública (/vagas/:slug, links do feed e do e-mail)
  // usa slug; o backend (detalhesVaga) resolve os dois.
  obterDetalhes: (id: number | string) =>
    api.get(`/api/vagas/${id}`),
  
  editar: (id: number, vaga: any) =>
    api.put(`/api/vagas/${id}`, vaga),
  
  deletar: (id: number) =>
    api.delete(`/api/vagas/${id}`),
  
  // novaDataServico é usado ao reabrir uma vaga cuja data já passou (o backend recusa
  // reabrir sem isso, ver ReabrirModal).
  atualizarStatus: (id: number, status: string, novaDataServico?: string) =>
    api.patch(`/api/vagas/${id}/status`, { status, novaDataServico }),
  
  listarDaEmpresa: () =>
    api.get('/api/empresas/vagas'),
};

// Candidaturas API
export const candidaturasAPI = {
  candidatar: (vagaId: number, mensagem?: string) =>
    api.post(`/api/vagas/${vagaId}/candidatar`, { mensagem }),

  listarDoProfissional: () =>
    api.get('/api/profissionais/candidaturas'),

  listarTimesDoProfissional: () =>
    api.get('/api/profissionais/candidaturas-times'),

  listarSubstituicoesDoProfissional: () =>
    api.get('/api/profissionais/candidaturas-substituicoes'),

  listarCandidatos: (vagaId: number, params?: { page?: number; limit?: number }) =>
    api.get(`/api/vagas/${vagaId}/candidatos`, { params }),

  alterarStatus: (candidaturaId: number, status: string, feedback?: string) =>
    api.patch(`/api/candidaturas/${candidaturaId}/status`, { status, feedback }),
};

// Favoritos API
export const favoritosAPI = {
  salvar: (vagaId: number) =>
    api.post(`/api/vagas/${vagaId}/salvar`),
  
  listar: () =>
    api.get('/api/profissionais/vagas-salvas'),
};

// Perfis API
export const perfisAPI = {
  obterPerfilProfissional: () =>
    api.get('/api/profissionais/perfil'),
  
  atualizarPerfilProfissional: (dados: any) =>
    api.put('/api/profissionais/perfil', dados),
  
  obterPerfilEmpresa: () =>
    api.get('/api/empresas/perfil'),
  
  atualizarPerfilEmpresa: (dados: any) =>
    api.put('/api/empresas/perfil', dados),
  
  obterEstatisticasEmpresa: () =>
    api.get('/api/empresas/estatisticas'),
};

// Upload API
export const uploadAPI = {
  curriculo: (arquivo: File) => {
    const formData = new FormData();
    formData.append('curriculo', arquivo);
    return api.post('/api/upload/curriculo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  logo: (arquivo: File) => {
    const formData = new FormData();
    formData.append('logo', arquivo);
    return api.post('/api/upload/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  downloadCurriculo: (id: number) =>
    api.get(`/api/download/curriculo/${id}`, {
      responseType: 'blob',
    }),

  fotoPerfil: (arquivo: File) => {
    const formData = new FormData();
    formData.append('foto', arquivo);
    return api.post('/api/upload/foto-perfil', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Ledger API
export const ledgerAPI = {
  listarTrabalhos: (params?: { mes?: number; ano?: number; status?: string }) =>
    api.get('/api/ledger/trabalhos', { params }),

  criarTrabalho: (dados: {
    titulo: string;
    clienteNome: string;
    clienteTelefone?: string;
    clienteEmail?: string;
    dataServico: string;
    dataVencimento?: string;
    valor: number;
  }) => api.post('/api/ledger/trabalhos', dados),

  editarTrabalho: (id: number, dados: {
    titulo?: string;
    clienteNome?: string;
    clienteTelefone?: string;
    clienteEmail?: string;
    dataServico?: string;
    dataVencimento?: string;
    valor?: number;
    status?: 'EM_ABERTO' | 'PAGO' | 'CANCELADO';
  }) => api.put(`/api/ledger/trabalhos/${id}`, dados),

  deletarTrabalho: (id: number) =>
    api.delete(`/api/ledger/trabalhos/${id}`),

  obterResumo: (params?: { mes?: number; ano?: number }) =>
    api.get('/api/ledger/resumo', { params }),

  obterConfiguracoes: () =>
    api.get('/api/ledger/configuracoes'),

  salvarConfiguracoes: (dados: {
    nomeExibicao: string;
    templateWhatsApp?: string;
    templateEmailAssunto?: string;
    templateEmailCorpo?: string;
  }) => api.put('/api/ledger/configuracoes', dados),

  sincronizar: () => api.post('/api/ledger/sincronizar'),
};

// Agenda API (Agendamentos + Cadastros)
export const agendaAPI = {
  // Cadastros
  listarCadastros: (params?: { tipoCadastro?: 'CLIENTE' | 'PRESTADOR' }) =>
    api.get('/api/agenda/cadastros', { params }),

  criarCadastro: (dados: {
    tipo: 'CLIENTE' | 'PRESTADOR';
    nome: string;
    whatsapp?: string;
    email?: string;
    observacoes?: string;
  }) => api.post('/api/agenda/cadastros', dados),

  editarCadastro: (id: number, dados: {
    tipo?: 'CLIENTE' | 'PRESTADOR';
    nome?: string;
    whatsapp?: string;
    email?: string;
    observacoes?: string;
  }) => api.put(`/api/agenda/cadastros/${id}`, dados),

  deletarCadastro: (id: number) =>
    api.delete(`/api/agenda/cadastros/${id}`),

  // Agendamentos
  listarAgendamentos: (params?: { mes?: number; ano?: number }) =>
    api.get('/api/agenda/agendamentos', { params }),

  proximosAgendamentos: () =>
    api.get('/api/agenda/agendamentos/proximos'),

  agendamentosPendentesConfirmacao: () =>
    api.get('/api/agenda/agendamentos/pendentes-confirmacao'),

  criarAgendamento: (dados: {
    titulo: string;
    local?: string;
    endereco?: string;
    valor?: number;
    data: string;
    horaInicio: string;
    horaFim: string;
    observacoes?: string;
    avisos?: { quantidade: number; unidade: string }[];
    cadastroId?: number;
  }) => api.post('/api/agenda/agendamentos', dados),

  editarAgendamento: (id: number, dados: {
    titulo?: string;
    local?: string;
    endereco?: string;
    valor?: number;
    data?: string;
    horaInicio?: string;
    horaFim?: string;
    observacoes?: string;
    avisos?: { quantidade: number; unidade: string }[];
    cadastroId?: number | null;
  }) => api.put(`/api/agenda/agendamentos/${id}`, dados),

  deletarAgendamento: (id: number) =>
    api.delete(`/api/agenda/agendamentos/${id}`),

  concluirAgendamento: (id: number, transferirParaLedger: boolean) =>
    api.patch(`/api/agenda/agendamentos/${id}/concluir`, { transferirParaLedger }),

  enviarEmailTeste: () =>
    api.post('/api/agenda/test-email'),
};

// Admin API (acesso restrito à conta administrativa)
export interface AdminListParams {
  page?: number;
  limit?: number;
  profissao?: string;
}

export const adminAPI = {
  dashboard: () =>
    api.get('/api/admin/dashboard'),
  usuarios: (params?: AdminListParams) =>
    api.get('/api/admin/usuarios', { params }),
  empresas: (params?: AdminListParams) =>
    api.get('/api/admin/empresas', { params }),
  profissionais: (params?: AdminListParams) =>
    api.get('/api/admin/profissionais', { params }),
  vagas: (params?: AdminListParams) =>
    api.get('/api/admin/vagas', { params }),
  candidaturas: (params?: AdminListParams) =>
    api.get('/api/admin/candidaturas', { params }),
  times: (params?: AdminListParams) =>
    api.get('/api/admin/times', { params }),
  candidaturasTime: (params?: AdminListParams) =>
    api.get('/api/admin/candidaturas-time', { params }),
  substituicoes: (params?: AdminListParams) =>
    api.get('/api/admin/substituicoes', { params }),
  candidaturasSubstituicao: (params?: AdminListParams) =>
    api.get('/api/admin/candidaturas-substituicao', { params }),
  agendamentos: (params?: AdminListParams) =>
    api.get('/api/admin/agendamentos', { params }),
};

// Avaliações — mútua entre as duas partes de uma candidatura aceita (vaga/time/
// substituição), liberada só depois que a data do serviço passa.
export type TipoAvaliacao = 'vaga' | 'time' | 'substituicao';

export interface CriarAvaliacaoData {
  tipo: TipoAvaliacao;
  referenciaId: number;
  nota: number;
  comentario?: string;
}

export const avaliacaoAPI = {
  pendentes: () =>
    api.get('/api/avaliacoes/pendentes'),
  feitas: () =>
    api.get('/api/avaliacoes/feitas'),
  recebidasPorUsuario: (userId: number, params?: { page?: number; limit?: number }) =>
    api.get(`/api/avaliacoes/usuario/${userId}`, { params }),
  criar: (data: CriarAvaliacaoData) =>
    api.post('/api/avaliacoes', data),
  editar: (id: number, data: { nota?: number; comentario?: string }) =>
    api.put(`/api/avaliacoes/${id}`, data),
  excluir: (id: number) =>
    api.delete(`/api/avaliacoes/${id}`),
};

export default api;